import React, { PropsWithChildren, type FC, useMemo } from 'react';
import { createBrowserRouter, RouterProvider as RP } from 'react-router-dom';
import {
  authenticatedRoutes,
  createRoomRoutes,
  gameRulesRoutes,
  inRoomRoutes,
  resetPasswordRoutes,
  signInRoutes,
  signUpRoutes,
  tocRoutes,
  verifyEmailRoutes,
} from '../screens';
import { guestRoutes } from '../screens/guest/routes';
import { useDeepLinks } from '../screens/guest/use-deep-links';
import { useIsAuth } from '../shared/hooks';
import { LoadingLayout, notFoundRoute } from '../shared/components';
import { useRouteSeo } from '../shared/seo';

const unAuthRoutes = [signUpRoutes, resetPasswordRoutes];

const alwaysIncludedRoutes = [
  signInRoutes,
  tocRoutes,
  notFoundRoute,
  gameRulesRoutes,
];

type RouteMode = 'account' | 'public';

const buildRouter = (mode: RouteMode) =>
  createBrowserRouter([
    // `/`, `/new`, `/join/:roomID`, `/table` work for visitors, guests and
    // accounts alike so invite links never dead-end.
    ...guestRoutes,
    ...(mode === 'account'
      ? [
          verifyEmailRoutes(),
          ...authenticatedRoutes,
          ...inRoomRoutes,
          createRoomRoutes(),
        ]
      : unAuthRoutes),
    ...alwaysIncludedRoutes,
  ]);

export const GameRoutes: FC<PropsWithChildren> = () => {
  const { isAuthenticated, isGuest, isLoading, isIdle } = useIsAuth();

  // Guests share the public route set, so signing in as a guest (or getting
  // a room token) never rebuilds the router and resets on-screen state.
  const mode: RouteMode = isAuthenticated && !isGuest ? 'account' : 'public';
  const router = useMemo(() => buildRouter(mode), [mode]);

  useDeepLinks(router);
  useRouteSeo(router);

  // Accounts keep the original behaviour of a full-screen loader whenever the
  // user is being (re)fetched. Guests only see it during the first boot.
  if (isIdle || (isLoading && !isGuest)) {
    return <LoadingLayout />;
  }

  return <RP router={router} fallbackElement={<LoadingLayout />} />;
};
